/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: tabs\PhonebookTab.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Provisioning Templates module.
----------------------------------------------------------------*/

import { BookOpen } from 'lucide-react';
import Combobox from '../../../components/ui/Combobox';
import { FormBox } from '../../../components/ui/FormBox';
import { useI18n } from '../../../hooks/useI18n';
import { PhonebookEntryConfig } from '../types';
import { extractPhonebookInfo } from '../utils/extractOptions';
import type * as api from '../api';

const generateId = () => Math.random().toString(36).substring(2) + Date.now().toString(36);

interface PhonebookTabProps {
  selectedModel: api.DeviceModel;
  phonebookEntries: PhonebookEntryConfig[];
  setPhonebookEntries: (updater: (prev: PhonebookEntryConfig[]) => PhonebookEntryConfig[]) => void;
  availablePhonebooks: { id?: number; uuid?: string; description: string; url?: string }[];
  setValue: (field: 'phonebook', value: PhonebookEntryConfig[]) => void;
}

export function PhonebookTab({
  selectedModel,
  phonebookEntries,
  setPhonebookEntries,
  availablePhonebooks,
  setValue,
}: PhonebookTabProps) {
  const { t } = useI18n();
  const pbInfo = extractPhonebookInfo(selectedModel.phonebook);

  if (pbInfo.count === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>{t('provisioningTemplates.noPhonebook', 'This model does not support Phonebook configuration.')}</p>
      </div>
    );
  }

  const fields = Object.entries(pbInfo.fields || {});

  const updatePhonebookAtIndex = (index: number, field: string, value: unknown) => {
    setPhonebookEntries(prev => {
      const updated = [...prev];
      while (updated.length <= index) { updated.push({ id: generateId() }); }
      updated[index] = { ...updated[index], [field]: value };
      setValue('phonebook', updated);
      return updated;
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderFieldInput = (index: number, fieldKey: string, fieldConfig: Record<string, any>) => {
    const currentEntry = phonebookEntries[index] || {};
    const value = currentEntry[fieldKey] !== undefined ? currentEntry[fieldKey] : '';

    if (fieldKey === 'url') {
      const phonebookOptions = availablePhonebooks.map(pb => ({
        label: pb.description,
        value: pb.url || pb.uuid,
        checked: false,
      }));
      const optionsWithCheck = phonebookOptions.map(opt => ({ ...opt, checked: opt.value === value }));
      return (
        <div className="flex items-center gap-1">
          <div className="w-48 flex-none">
            <Combobox single options={optionsWithCheck} onToggle={(val) => updatePhonebookAtIndex(index, fieldKey, val)}
              placeholder={t('provisioningTemplates.selectPhonebook', 'Select Phonebook...')} className="w-full" />
          </div>
          <input type="text" value={value} onChange={(e) => updatePhonebookAtIndex(index, fieldKey, e.target.value)}
            className="flex-1 px-2 py-1 border border-border rounded text-sm bg-background"
            placeholder={t(`provisioningTemplates.${fieldKey}`, fieldKey)} />
        </div>
      );
    }

    if (fieldConfig.type === 'select' && fieldConfig.values) {
      const options = Object.entries(fieldConfig.values).map(([label, val]) => ({ label, value: String(val) }));
      return (
        <select value={value} onChange={(e) => updatePhonebookAtIndex(index, fieldKey, e.target.value)}
          className="w-full px-2 py-1 border border-border rounded text-sm bg-background">
          <option value="">{t('common.select', 'Select')}</option>
          {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
      );
    }

    if (fieldConfig.type === 'boolean') {
      return (
        <select value={String(value)} onChange={(e) => updatePhonebookAtIndex(index, fieldKey, e.target.value === 'true')}
          className="w-full px-2 py-1 border border-border rounded text-sm bg-background">
          <option value="false">{t('common.no', 'No')}</option>
          <option value="true">{t('common.yes', 'Yes')}</option>
        </select>
      );
    }

    return (
      <input type={fieldConfig.type === 'password' ? 'password' : 'text'} value={value}
        onChange={(e) => updatePhonebookAtIndex(index, fieldKey, e.target.value)}
        className="w-full px-2 py-1 border border-border rounded text-sm bg-background"
        placeholder={t(`provisioningTemplates.${fieldKey}`, fieldKey)} />
    );
  };

  return (
    <FormBox title={t('provisioningTemplates.tabs.phonebooks', 'Phonebook Configuration')} icon={<BookOpen className="h-4 w-4" />}>
      <div className="border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-3 py-2 text-left font-medium w-16">#</th>
              {fields.map(([key]) => (
                <th key={key} className="px-3 py-2 text-left font-medium">
                  {t(`provisioningTemplates.${key}`, key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()))}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: pbInfo.count }).map((_, index) => (
              <tr key={index} className="border-b border-border last:border-0 hover:bg-muted/30">
                <td className="px-3 py-2 font-medium text-muted-foreground">{index + 1}</td>
                {fields.map(([key, config]) => (
                  <td key={key} className="px-3 py-1">{renderFieldInput(index, key, config)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </FormBox>
  );
}
